import React, { useState } from 'react';
import { ArrowRight, Mail, Bell } from 'lucide-react';
import { validateEmail } from '../lib/validation/email';
import { saveSubscriber } from '../lib/subscribers';
import { Footer } from './Footer';

interface LandingPageProps {
  onStart: () => void;
}

export function LandingPage({ onStart }: LandingPageProps) {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateEmail(email);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      setIsSubmitting(true);
      await saveSubscriber(email);
      localStorage.setItem('userEmail', email);
      onStart();
    } catch (error) {
      setErrors(['Failed to save email. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsTyping(true);
    
    if (errors.length > 0) {
      setErrors([]);
    }

    const timeoutId = setTimeout(() => {
      if (newEmail) {
        const validation = validateEmail(newEmail);
        if (!validation.isValid) {
          setErrors(validation.errors);
        }
      }
      setIsTyping(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow flex items-center justify-center py-16 md:py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-12">
            <img 
              src="/logo.svg"
              alt="Tell Us Your Story"
              className="w-16 h-16"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-8">
            Share Your Story With Us
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 leading-relaxed">
            Every story matters. Whether it's a team achievement, a memorable event, or a significant milestone,
            we're here to help you document and share your journey.
          </p>
          
          <div className="mb-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-md mx-auto">
            <div className="flex items-start space-x-3">
              <Bell className="w-5 h-5 text-[#60a3bc] mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-600 dark:text-gray-300 text-left">
                We update our features regularly and want to keep you informed about new capabilities. 
                By providing your email, you'll receive important updates and learn more about what we're building.
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email to get started"
                className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-colors duration-200
                  ${errors.length > 0 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 focus:border-[#60a3bc] focus:ring-[#60a3bc]'}
                  focus:ring-2 focus:ring-opacity-50`}
                disabled={isSubmitting}
              />
              {isTyping && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#60a3bc] border-t-transparent"></div>
                </div>
              )}
            </div>
            
            {errors.length > 0 && (
              <div className="space-y-1">
                {errors.map((error, index) => (
                  <p key={index} className="text-red-500 text-sm">{error}</p>
                ))}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-8 py-4 rounded-full bg-[#60a3bc] text-white hover:bg-[#4a8ba3] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  Start Writing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}